import { Message } from "element-ui";
import PasswordSettings from '~/api/password_settings';
import SecuritySettings from '~/api/security_questions';
import ErrorUtils from '~/utils/error_utils';

export default {
    async getPasswordSettings({ commit }) {
        commit('updateLoading', {
            passwordSettings: true
        });

        try {
            const data = await PasswordSettings.getPasswordSettings();

            commit('updateSettingsForm', data.settings);
            commit('updateMfaSettingsForm', data.mfa_settings);
            commit('updateAllAdminUsers', data.all_admin_users);
            commit('setHasPolicy', data.has_policy);
            commit('setHasSamlProvider', data.has_saml_provider);
            commit('setAllowNativeLogin', data.allow_native_login);

            if (data.org_security_question_settings !== null) {
                commit('updateOrgSecurityQuestionSettingsForm', data.org_security_question_settings);
            }

            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: 'Unable to retrieve required data'
            });
        } finally {
            commit('updateLoading', {
                passwordSettings: false
            });
        }
    },

    async getPasswordRules({ commit }, payload) {
        commit('updateLoading', {
            passwordRules: true
        });

        try {
            const token = (typeof payload !== "undefined" && payload.length > 0) ? payload : "";
            const data = await PasswordSettings.getPasswordRules(token);
            commit('setPasswordRules', data.rules);
            return data;
        } catch (error) {
            throw error;
        } finally {
            commit('updateLoading', {
                passwordRules: false
            });
        }
    },

    async getPasswordRulesFromUserEmail({commit}, payload) {
        commit('updateLoading', {
            passwordRules: true
        });

        try {
            const email = (typeof payload !== "undefined" && payload.length > 0) ? payload : "";
            const data = await PasswordSettings.getPasswordRulesFromUserEmail(email);
            commit('setPasswordRules', data.rules);
            return data;
        } catch (error) {
            throw error;
        } finally {
            commit('updateLoading', {
                passwordRules: false
            });
        }
    },

    async postPasswordSettingsForm({ commit, state }) {
        commit('updateLoading', {
            formPost: true
        });

        try {
            const data = await PasswordSettings.postPasswordSettingsForm({
                ...state.settingsForm,
                has_policy: state.hasPolicy,
                has_saml_provider: state.hasSamlProvider
            });
            Message({
                type: 'success',
                message: data.msg
            });
            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                formPost: false
            });
        }
    },

    async postMfaSettings({commit, state}) {
        commit('updateLoading', {
            mfaSettings: true
        });

        try {
            const data = await PasswordSettings.postMfaSettings({
                ...state.mfaSettingsForm
            });

            Message.success(data.msg);
        } catch (error) {
            Message.error(error.message);
        } finally {
            commit('updateLoading', {
                mfaSettings: false
            });
        }
    },

    async postSetPassword({ commit, state }) {
        commit('updateLoading', {
            formPost: true
        });

        try {
            const data = await PasswordSettings.postSetPassword({
                ...state.passwordForm,
                token: state.redemptionToken
            });

            Message({
                type: 'success',
                message: 'Password successfully updated',
                duration: 1000,
                onClose: () => {
                    window.location.replace(data.redirect_url);
                }
            });

            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: error.responseJSON.msg
            });
        } finally {
            commit('updateLoading', {
                formPost: false
            });
        }
    },

    async postUpdateExpiredPassword({commit, state}) {
        return await PasswordSettings.updateExpiredPassword({
            ...state.passwordForm
        });
    },

    async postOrgSecurityQuestionSettingsForm({commit, state}) {
        commit('updateLoading', {
            orgSecurityQuestions: true
        });

        try {
            const data = await PasswordSettings.postOrgSecurityQuestionSettingsForm({
                ...state.orgSecurityQuestionSettingsForm
            });
            Message({
                type: 'success',
                message: data.msg
            });
            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                orgSecurityQuestions: false
            });
        }
    },

    async getOrgSecurityQuestions({commit}, retrieveAll = true) {
        commit('updateLoading', {
            securityQuestions: true
        });

        try {
            const data = await SecuritySettings.getOrgSecurityQuestions(retrieveAll);
            commit('setAnswerSecurityQuestionsForm', data);
            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                securityQuestions: false
            });
        }
    },

    async getOrgSecurityQuestionsForm({commit}) {
        commit('updateLoading', {
            orgSecurityQuestions: true
        });

        try {
            const data = await PasswordSettings.getOrgSecurityQuestionsForm();
            commit('setOrgSecurityQuestionsForm', data);
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                orgSecurityQuestions: false
            });

            commit('updateDialogVisible', {
                orgSecurityQuestions: true
            });
        }
    },

    async postOrgSecurityQuestionsForm({commit, state}) {
        commit('updateLoading', {
            orgSecurityQuestions: true
        });

        try {
            const data = await PasswordSettings.postOrgSecurityQuestionsForm({
                ...state.orgSecurityQuestionsForm
            });
            Message({
                type: 'success',
                message: data.msg
            });
            return data;
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                orgSecurityQuestions: false
            });
        }
    },

    async postAnswerSecurityQuestions({commit, state}, token) {
        commit('updateLoading', {
            answerSecurityQuestions: true
        });

        try {
            const response = await PasswordSettings.postAnswerSecurityQuestionsForm({
                ...state.answerSecurityQuestionsForm,
                token: token
            });

            Message({
                type: 'success',
                message: 'Security questions submitted'
            });

            return response;
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                answerSecurityQuestions: false
            });
        }
    },

    async setRecaptchaSiteKey({commit}) {
        commit('updateLoading', {
            recaptcha: true
        });

        try {
            const data = await PasswordSettings.getRecaptchaSiteKey();
            commit('setRecaptchaSiteKey', data.siteKey);
        } catch (error) {
            throw error;
        } finally {
            commit('updateLoading', {
                recaptcha: false
            });
        }
    },

    async validateRecaptcha({commit, state}, responseToken) {
        commit('updateLoading', {
            answerSecurityQuestions: true
        });

        try {
            return await PasswordSettings.validateRecaptcha({
                recaptchaResponseToken: responseToken
            });
        } catch (error) {
            Message({
                type: 'error',
                message: error
            });
        } finally {
            commit('updateLoading', {
                answerSecurityQuestions: false
            });
        }
    }
}
